import { useCallback, useEffect, useRef, useState } from 'react';
import type { ShipState } from '../../../lib/englishApi.js';
import { useSfx } from '../../../lib/sfx/index.js';
import { iconSvg, rocketSvg } from './shipArt.js';
import { seedFlightStars } from './orbitScene.js';
import { SHIP_PIECES, pieceById, type ShipPieceId } from './pieces.js';
import { prefersReducedMotion } from './motion.js';
import { ShipScene, type ShipSceneHandle } from './ShipScene.js';
import './ship.css';

const wait = (ms: number) => new Promise<void>((ok) => setTimeout(ok, ms));
const Icon = ({ name, cls = '' }: { name: Parameters<typeof iconSvg>[0]; cls?: string }) => (
  <span className={cls} dangerouslySetInnerHTML={{ __html: iconSvg(name) }} />
);

export interface ShipHomeProps {
  ship: ShipState;
  variant: 'mobile' | 'desktop';
  /** Lleva a la actividad de una pieza. */
  go: (id: ShipPieceId, how?: { choose?: boolean }) => Promise<void>;
  markSeen: (pieces: readonly ShipPieceId[]) => Promise<void>;
  /** Registra el despegue (el servidor verifica las 7). */
  launch: () => Promise<ShipState>;
  onFreeTalk: () => void;
  /** «Ver el Inicio completo»: las tarjetas de siempre, por esta visita. */
  onShowFullHome: () => void;
  /**
   * Quien ya despegó una vez y repite el recorrido (Emmanuel, 20/10): «Saltear» lo devuelve a su nave
   * armada, como estaba. Sin esto (primera vez), el pie ofrece mirar el Inicio completo.
   */
  onSkip?: (() => void) | undefined;
  /** El despegue arrancó: el Inicio tiene que sostener esta pantalla hasta el final. */
  onLaunchStart: () => void;
  /** «Ir a mi Inicio» después de «¡Despegaste!». */
  onLaunched: () => void;
}

/**
 * El Inicio mientras se arma la nave (encargo 117): el hangar con la escena tocable, la tarjeta de
 * la pieza de hoy (la primera que falta) y el pie con «Charla libre» y «Ver el Inicio completo».
 * En la compu, además, la lista de las 7. Las piezas ganadas desde la última visita entran en
 * espiral, de a una, con el cartel «¡Pieza nueva!»; con las 7 aparece «Despegar».
 */
export function ShipHome(props: ShipHomeProps) {
  const { ship, variant, go, markSeen, launch, onFreeTalk, onShowFullHome } = props;
  const { play } = useSfx();
  const scene = useRef<ShipSceneHandle | null>(null);
  const hangar = useRef<HTMLDivElement>(null);
  const layer = useRef<HTMLDivElement>(null);
  // Lo que la escena muestra al montar: lo hecho menos lo nuevo (lo nuevo entra en espiral).
  const [placedAtMount] = useState<ShipPieceId[]>(() =>
    ship.pieces.filter((p) => p.done && !ship.fresh.includes(p.id)).map((p) => p.id),
  );
  const [inScene, setInScene] = useState<ReadonlySet<ShipPieceId>>(() => new Set(placedAtMount));
  const [banner, setBanner] = useState<{ id: ShipPieceId; leaving: boolean } | null>(null);
  const [phase, setPhase] = useState<'home' | 'launching' | 'final'>('home');
  const busy = useRef(false);
  const completeShown = useRef(false);

  // Las piezas hechas que todavía no están en la escena entran de a una.
  useEffect(() => {
    const pending = ship.pieces.filter((p) => p.done && !inScene.has(p.id)).map((p) => p.id);
    if (pending.length === 0 || busy.current) return;
    busy.current = true;
    void (async () => {
      await wait(500);
      for (const id of pending) {
        setBanner({ id, leaving: false });
        await scene.current?.win(id);
        setInScene((prev) => new Set(prev).add(id));
        await wait(prefersReducedMotion() ? 300 : 900);
        setBanner({ id, leaving: true });
        await wait(400);
        setBanner(null);
      }
      const seen = pending.filter((id) => ship.fresh.includes(id));
      await markSeen(seen).catch((err: unknown) =>
        console.warn('[ship] no se marcó lo visto', err),
      );
      busy.current = false;
    })();
  }, [ship, inScene, markSeen]);

  const allIn = inScene.size === 7;
  useEffect(() => {
    if (!allIn || completeShown.current) return;
    completeShown.current = true;
    scene.current?.complete();
    // La fanfarria sólo si la séptima acaba de entrar (no cada vez que vuelve al Inicio).
    if (placedAtMount.length < 7) play('ship-complete');
  }, [allIn, placedAtMount.length, play]);

  // La tarjeta de hoy avanza con la escena: mientras una pieza entra en espiral, todavía no cuenta.
  const next = SHIP_PIECES.find((p) => !inScene.has(p.id)) ?? null;

  const onPieceTap = useCallback(
    (id: ShipPieceId, placed: boolean) => {
      // Tocar una que falta lleva a su actividad (después de leer el globo).
      if (!placed && phase === 'home') window.setTimeout(() => void go(id), 900);
    },
    [go, phase],
  );

  const startLaunch = async () => {
    if (phase !== 'home') return;
    try {
      await launch();
    } catch (err) {
      console.warn('[ship] no se pudo despegar', err);
      return;
    }
    props.onLaunchStart();
    setPhase('launching');
  };

  // El despegue: el hangar crece a pantalla completa y la escena hace lo suyo.
  useEffect(() => {
    if (phase !== 'launching') return;
    void (async () => {
      const h = hangar.current;
      const l = layer.current;
      if (!h || !l || !scene.current) return;
      const a = h.getBoundingClientRect();
      h.classList.add('is-fullscreen');
      const b = h.getBoundingClientRect();
      if (!prefersReducedMotion()) {
        await h.animate(
          [
            {
              transform: `translate(${a.left - b.left}px, ${a.top - b.top}px) scale(${a.width / b.width}, ${a.height / b.height})`,
              borderRadius: '22px',
            },
            { transform: 'none', borderRadius: '0px' },
          ],
          { duration: 500, easing: 'cubic-bezier(.3,.7,.2,1)' },
        ).finished;
      }
      await scene.current.launch(l);
      setPhase('final');
    })();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'final' || !layer.current) return;
    const stars = layer.current.querySelector<HTMLElement>('.ship-flight .ship-stars');
    if (stars && stars.childElementCount === 0) seedFlightStars(stars);
  }, [phase]);

  const bannerPiece = banner ? pieceById(banner.id) : null;
  const hangarCard = (
    <div ref={hangar} className="h2-card ship-hangar">
      <div className="ship-head">
        <span className="ship-k">Tu primer recorrido</span>
        <span className="ship-n">
          <b>{inScene.size}</b> de 7 piezas
        </span>
      </div>
      <div className="ship-title">Armá tu nave</div>
      <ShipScene ref={scene} placed={placedAtMount} onPieceTap={onPieceTap} />
      {bannerPiece && (
        <div className={`ship-banner${banner?.leaving ? ' is-leaving' : ''}`} role="status">
          <Icon name={bannerPiece.medium} cls={`ship-ic ship-c-${bannerPiece.medium}`} />
          <span>
            <small>¡Pieza nueva!</small>
            <b>{bannerPiece.name}</b>
            <em>{bannerPiece.purpose}</em>
          </span>
        </div>
      )}
    </div>
  );

  const todayCard = (
    <div className="h2-card ship-today">
      <span className="h2-badge ship-badge">
        {next
          ? `Pieza ${SHIP_PIECES.indexOf(next) + 1} de 7 · ${next.name}`
          : 'Todo listo · 7 de 7'}
      </span>
      <div className="h2-title">{next ? next.title : 'Encendé los motores'}</div>
      <p className="h2-desc">
        {next ? next.task : 'Juntaste las 7 herramientas para tu viaje al inglés.'}
      </p>
      {next ? (
        <div className="ship-today-acts">
          <button type="button" className="h2-cta ship-today-cta" onClick={() => void go(next.id)}>
            {next.cta}
          </button>
          {next.id === 'antena' && (
            <button
              type="button"
              className="ship-today-alt"
              onClick={() => void go('antena', { choose: true })}
            >
              Elegir yo
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          className="h2-cta ship-today-cta is-pulse"
          onClick={() => void startLaunch()}
          disabled={!allIn || phase !== 'home'}
        >
          <Icon name="rocket" />
          Despegar
        </button>
      )}
    </div>
  );

  const foot = (
    <div className="ship-foot">
      <button type="button" className="ship-pill is-lime" onClick={onFreeTalk}>
        <Icon name="charla" />
        Charla libre
      </button>
      {props.onSkip ? (
        <button type="button" className="ship-pill is-link" onClick={props.onSkip}>
          Saltear el recorrido ›
        </button>
      ) : (
        <button type="button" className="ship-pill is-link" onClick={onShowFullHome}>
          Ver el Inicio completo ›
        </button>
      )}
    </div>
  );

  const list = (
    <div className="h2-card ship-list-card">
      <div className="ship-k">Las 7 piezas de tu nave</div>
      <ul className="ship-list">
        {SHIP_PIECES.map((p) => {
          const isDone = inScene.has(p.id);
          const cls = isDone ? ' is-done' : next?.id === p.id ? ' is-next' : '';
          return (
            <li key={p.id} className={`ship-li ship-c-${p.medium}${cls}`}>
              <Icon name={isDone ? 'check' : p.medium} cls="ship-ic" />
              <span>
                <b>{p.name}</b>
                <small>{p.feature}</small>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const flightLayer = phase !== 'home' && (
    <div ref={layer} className="ship-layer">
      {phase === 'final' && (
        <div className="ship-final">
          <div className="ship-flight">
            <div className="ship-stars" />
            <div
              className="ship-flight-craft"
              dangerouslySetInnerHTML={{
                __html: rocketSvg({ fire: true, label: 'Tu nave, volando' }),
              }}
            />
          </div>
          <div className="ship-final-k">Nave completa</div>
          <div className="ship-final-t">¡Despegaste!</div>
          <p className="ship-final-s">
            Tenés todas las herramientas para empezar tu viaje hacia un mejor inglés.
          </p>
          <div className="ship-final-tools">
            {SHIP_PIECES.map((p) => (
              <span key={p.id} className={`ship-c-${p.medium}`}>
                <Icon name={p.medium} cls="ship-ic" />
                {p.name}
              </span>
            ))}
          </div>
          <button type="button" className="ship-cta" onClick={props.onLaunched}>
            Ir a mi Inicio
          </button>
        </div>
      )}
    </div>
  );

  if (variant === 'desktop') {
    return (
      <div className="ship-desk">
        {hangarCard}
        <div className="ship-desk-side">
          {todayCard}
          {list}
          {foot}
        </div>
        {flightLayer}
      </div>
    );
  }
  return (
    <div className="ship-wrap">
      {hangarCard}
      {todayCard}
      {foot}
      {flightLayer}
    </div>
  );
}
